import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Menu, X } from "lucide-react";
import { COMPANY, NAV_LINKS } from "@/lib/constants";

export const Header = () => {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 8);
        onScroll();
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const handleNav = (href) => {
        setOpen(false);
        const id = href.replace("#", "");
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    return (
        <header
            data-testid="site-header"
            className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
                scrolled ? "bg-bone/95 backdrop-blur border-b border-line" : "bg-transparent"
            }`}
        >
            <div className="max-w-7xl mx-auto px-6 md:px-12 h-16 md:h-20 flex items-center justify-between">
                <a
                    href="#top"
                    data-testid="logo-link"
                    onClick={(e) => {
                        e.preventDefault();
                        window.scrollTo({ top: 0, behavior: "smooth" });
                    }}
                    className="flex items-center gap-2 group"
                >
                    <span className="inline-flex h-9 w-9 items-center justify-center bg-sage text-bone font-heading font-semibold text-sm tracking-tight">
                        H<span className="text-terracotta">₂</span>N
                    </span>
                    <span className="font-heading text-base md:text-lg font-medium tracking-tight text-ink">
                        {COMPANY.shortName}
                        <span className="text-stone font-normal">, LLC</span>
                    </span>
                </a>

                <nav className="hidden lg:flex items-center gap-10">
                    {NAV_LINKS.map((l) => (
                        <button
                            key={l.href}
                            data-testid={`nav-link-${l.label.toLowerCase()}`}
                            onClick={() => handleNav(l.href)}
                            className="text-sm font-medium text-ink/80 hover:text-terracotta transition-colors tracking-tight"
                        >
                            {l.label}
                        </button>
                    ))}
                </nav>

                <div className="flex items-center gap-3">
                    <Button
                        data-testid="header-cta-quote"
                        onClick={() => handleNav("#contact")}
                        className="hidden md:inline-flex rounded-none bg-sage hover:bg-sage-dark text-bone font-medium px-5 h-10 text-sm"
                    >
                        Request Quote
                    </Button>
                    <button
                        data-testid="mobile-menu-toggle"
                        onClick={() => setOpen((v) => !v)}
                        className="lg:hidden inline-flex h-10 w-10 items-center justify-center border border-line text-ink"
                        aria-label="Menu"
                    >
                        {open ? <X size={18} /> : <Menu size={18} />}
                    </button>
                </div>
            </div>

            {/* Mobile menu */}
            {open && (
                <div data-testid="mobile-menu" className="lg:hidden border-t border-line bg-bone">
                    <div className="px-6 py-6 flex flex-col gap-4">
                        {NAV_LINKS.map((l) => (
                            <button
                                key={l.href}
                                onClick={() => handleNav(l.href)}
                                data-testid={`mobile-nav-${l.label.toLowerCase()}`}
                                className="text-left text-base font-medium text-ink py-1"
                            >
                                {l.label}
                            </button>
                        ))}
                        <Button
                            data-testid="mobile-cta-quote"
                            onClick={() => handleNav("#contact")}
                            className="mt-2 rounded-none bg-sage hover:bg-sage-dark text-bone font-medium h-11"
                        >
                            Request Quote
                        </Button>
                    </div>
                </div>
            )}
        </header>
    );
};
